// AdminStationGroupsLayout — správa uložených skupin stanic (jen pro is_admin = 'Y'), vzor AdminUsersLayout.jsx
// Viz doc/plans/station-group-layout.md sekce VI a doc/plans/admin-station-groups-inline-edit.md
import { useState, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '../api/adminApi'
import { useAuthStore } from '../store/useAuthStore'
import { useSavedStationGroupFilters } from '../hooks/useStationGroups'
import { GROUP_PRODUCTS } from '../config/productRegistry'
import { CopyProductFilterModal } from '../components/filters/CopyProductFilterModal'
import { useTrackView } from '../hooks/useTrackProduct'

const PRODUCT_LABEL = new Map(GROUP_PRODUCTS.flatMap(g => g.items.map(item => [item.slug, item.label])))

function InlineEditableCell({ value, onSave, placeholder = '—', inputType = 'text' }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => { setDraft(value ?? ''); setEditing(true) }}
        className="text-left w-full px-1 py-0.5 rounded hover:bg-blue-50 transition-colors"
      >
        {value === null || value === undefined || value === '' ? <span className="text-gray-400">{placeholder}</span> : value}
      </button>
    )
  }

  const commit = () => {
    setEditing(false)
    if (draft === value) return
    if (inputType === 'number') {
      const n = Number(draft)
      if (!Number.isInteger(n)) return
      onSave(n)
    } else {
      onSave(draft)
    }
  }

  return (
    <input
      autoFocus
      type={inputType === 'number' ? 'number' : 'text'}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') e.target.blur()
        if (e.key === 'Escape') setEditing(false)
      }}
      className="w-full px-1 py-0.5 rounded border border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-400"
    />
  )
}

function GroupFiltersRow({ group, colSpan, navigate }) {
  const { filters, isLoading } = useSavedStationGroupFilters(group.id)
  const qc = useQueryClient()
  const [confirmDelFilter, setConfirmDelFilter] = useState(null)
  const [copyFrom, setCopyFrom] = useState(null)

  const invalidateFilters = () => qc.invalidateQueries({ queryKey: ['saved-station-group-filters', group.id] })

  const deleteFilterMutation = useMutation({
    mutationFn: (productCode) => adminApi.deleteGroupProductFilter(group.id, productCode),
    onSuccess: () => { invalidateFilters(); setConfirmDelFilter(null) },
  })

  const addFilterMutation = useMutation({
    mutationFn: (productCode) => adminApi.saveGroupProductFilter(group.id, productCode, group.group_filter || {}),
    onSuccess: invalidateFilters,
  })

  const copyFilterMutation = useMutation({
    mutationFn: async ({ filter, targetCodes }) => {
      await Promise.all(targetCodes.map(code => adminApi.saveGroupProductFilter(group.id, code, filter)))
    },
    onSuccess: invalidateFilters,
  })

  const filterByProductCode = new Map(filters.map(f => [f.product_code, f]))

  // Obě sekce (uložené i chybějící filtry) se řadí a člení podle stejných kategorií jako
  // GROUP_PRODUCTS (Přehled / 10minutová / Hodinová / Denní data), aby bylo jasné, o který
  // produkt jde, i když se stejný název (např. "Teplota") opakuje napříč granularitami.
  const filterGroups = GROUP_PRODUCTS
    .map(g => ({ group: g.group, items: g.items.filter(item => filterByProductCode.has(item.slug)) }))
    .filter(g => g.items.length > 0)

  const missingGroups = GROUP_PRODUCTS
    .map(g => ({ group: g.group, items: g.items.filter(item => !filterByProductCode.has(item.slug)) }))
    .filter(g => g.items.length > 0)

  const openInEditor = (productCode) => {
    navigate(`/station-group/${productCode}`, { state: { savedGroupId: group.id, editMode: true, jumpToTab: 'filters' } })
  }

  return (
    <Fragment>
    <tr>
      <td colSpan={colSpan} className="px-4 py-3 bg-gray-50">
        {isLoading && <p className="text-xs text-gray-400">Načítám filtry…</p>}

        {!isLoading && (
          <div className="flex flex-col gap-3">
            {filters.length === 0 && (
              <p className="text-xs text-gray-400">Žádné uložené filtry k produktům.</p>
            )}

            {filters.length > 0 && (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-400">
                    <th className="text-left font-medium py-1">Produkt</th>
                    <th className="text-left font-medium py-1">Uloženo</th>
                    <th className="text-right font-medium py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {filterGroups.map(g => (
                    <Fragment key={g.group}>
                      <tr>
                        <td colSpan={3} className="pt-2 pb-0.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                          {g.group}
                        </td>
                      </tr>
                      {g.items.map(item => {
                        const f = filterByProductCode.get(item.slug)
                        return (
                          <tr key={item.slug} className="border-t border-gray-200">
                            <td className="py-1.5">
                              <button
                                type="button"
                                onClick={() => openInEditor(item.slug)}
                                className="text-blue-600 hover:underline"
                                title="Otevřít filtr ve Skupině stanic"
                              >
                                {item.label}
                              </button>
                            </td>
                            <td className="py-1.5 text-gray-400">{f.updated_at ? new Date(f.updated_at).toLocaleString('cs-CZ') : '—'}</td>
                            <td className="py-1.5">
                              <div className="flex gap-1 justify-end">
                                <button
                                  type="button"
                                  title="Zkopírovat filtr do:"
                                  className="px-0.5 py-0.5 text-sm leading-none border border-blue-300 text-blue-600 hover:bg-blue-50 transition-colors"
                                  onClick={() => setCopyFrom(f)}
                                >
                                  📋
                                </button>
                                <button
                                  type="button"
                                  title="Smazat filtr"
                                  className="px-0.5 py-0.5 text-sm leading-none border border-red-300 text-red-600 hover:bg-red-50 transition-colors"
                                  onClick={() => setConfirmDelFilter(f)}
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            )}

            {missingGroups.length > 0 && (
              <div className="flex flex-col gap-1.5 pt-2 border-t border-gray-200">
                <span className="text-xs text-gray-400">Bez vlastního filtru:</span>
                {missingGroups.map(g => (
                  <div key={g.group} className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide w-28 shrink-0">{g.group}</span>
                    {g.items.map(item => (
                      <button
                        key={item.slug}
                        type="button"
                        disabled={addFilterMutation.isPending}
                        onClick={() => addFilterMutation.mutate(item.slug)}
                        title="Přidat filtr (zkopíruje se filtr skupiny)"
                        className="px-2 py-0.5 text-xs rounded border border-dashed border-gray-300 text-gray-500 hover:bg-white hover:border-blue-300 hover:text-blue-600 disabled:opacity-50 transition-colors"
                      >
                        + {item.label}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {addFilterMutation.isError && (
              <p className="text-xs text-red-600">{addFilterMutation.error?.message}</p>
            )}
          </div>
        )}
      </td>
    </tr>

    {confirmDelFilter && (
      <ConfirmDeleteFilterModal
        productLabel={PRODUCT_LABEL.get(confirmDelFilter.product_code) ?? confirmDelFilter.product_code}
        isPending={deleteFilterMutation.isPending}
        isError={deleteFilterMutation.isError}
        errorMessage={deleteFilterMutation.error?.message}
        onCancel={() => setConfirmDelFilter(null)}
        onConfirm={() => deleteFilterMutation.mutate(confirmDelFilter.product_code)}
      />
    )}

    {copyFrom && (
      <CopyProductFilterModal
        groupProducts={GROUP_PRODUCTS}
        sourceProductCode={copyFrom.product_code}
        sourceProductLabel={PRODUCT_LABEL.get(copyFrom.product_code) ?? copyFrom.product_code}
        onCopy={(targetCodes) => copyFilterMutation.mutateAsync({ filter: copyFrom.filter, targetCodes })}
        onClose={() => setCopyFrom(null)}
      />
    )}
    </Fragment>
  )
}

function ConfirmDeleteFilterModal({ productLabel, isPending, isError, errorMessage, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm flex flex-col gap-4">
        <h3 className="text-lg font-bold text-gray-800">Smazat filtr?</h3>
        <p className="text-sm text-gray-600">
          Opravdu chcete smazat uložený filtr pro produkt <strong>{productLabel}</strong>? Tato akce je nevratná.
        </p>
        {isError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{errorMessage}</p>
        )}
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">
            Zrušit
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            Smazat
          </button>
        </div>
      </div>
    </div>
  )
}

export function AdminStationGroupsLayout() {
  const navigate = useNavigate()
  // Sledování přístupů (etapa K2) — admin obrazovky jsou systémové části bez uzlu v registru.
  useTrackView('admin', 'admin-groups')
  const { user } = useAuthStore()
  const qc = useQueryClient()
  const [confirmDel, setConfirmDel] = useState(null)
  const [expandedId, setExpandedId] = useState(null)

  const fieldMutation = useMutation({
    mutationFn: ({ id, ...data }) => adminApi.updateSavedStationGroup(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-saved-station-groups'] }),
  })

  const activeMutation = useMutation({
    mutationFn: ({ id, is_active }) => adminApi.updateSavedStationGroup(id, { is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-saved-station-groups'] }),
  })

  if (user && user.is_admin !== 'Y') {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Přístup odepřen — potřebujete práva administrátora.
      </div>
    )
  }

  const { data: groups = [], isLoading, isError } = useQuery({
    queryKey: ['admin-saved-station-groups'],
    queryFn: adminApi.getSavedStationGroups,
    enabled: !!user,
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => adminApi.deleteSavedStationGroup(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-saved-station-groups'] }); setConfirmDel(null) },
  })

  const toggleExpand = (id) => setExpandedId(cur => cur === id ? null : id)

  const COL_COUNT = 6

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4">
        <button onClick={() => navigate('/')} className="text-gray-400 hover:text-gray-700 transition-colors text-lg">←</button>
        <h1 className="text-xl font-bold text-gray-800">Správa skupin stanic</h1>
        <span className="ml-auto text-sm text-gray-400">{groups.length} skupin</span>
        <button
          onClick={() => navigate('/station-group')}
          className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
        >
          + Nová skupina
        </button>
      </div>

      <div className="p-6">
        {isLoading && <p className="text-gray-400 text-sm">Načítám…</p>}
        {isError && <p className="text-red-500 text-sm">Chyba při načítání skupin.</p>}

        {!isLoading && !isError && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['', 'Název', 'Popis', 'Stav', 'Pořadí', ''].map((h, i) => (
                    <th key={i} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map(g => (
                  <Fragment key={g.id}>
                    <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleExpand(g.id)}
                          className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors text-xs"
                          title="Uložené filtry k produktům"
                        >
                          {expandedId === g.id ? '▼' : '▶'}
                        </button>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-800">
                        <InlineEditableCell
                          value={g.name}
                          onSave={(name) => fieldMutation.mutate({ id: g.id, name })}
                        />
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        <InlineEditableCell
                          value={g.description}
                          onSave={(description) => fieldMutation.mutate({ id: g.id, description })}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={g.is_active === 'Y'}
                            disabled={activeMutation.isPending}
                            onChange={() => activeMutation.mutate({ id: g.id, is_active: g.is_active === 'Y' ? 'N' : 'Y' })}
                            className="accent-blue-600"
                          />
                          <span className={`text-xs font-medium ${g.is_active === 'Y' ? 'text-green-700' : 'text-gray-500'}`}>
                            {g.is_active === 'Y' ? 'Aktivní' : 'Skryto'}
                          </span>
                        </label>
                      </td>
                      <td className="px-4 py-3 text-gray-400">
                        <InlineEditableCell
                          value={g.sort_order ?? 0}
                          inputType="number"
                          onSave={(sort_order) => fieldMutation.mutate({ id: g.id, sort_order })}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => navigate('/station-group', { state: { savedGroupId: g.id, editMode: true } })}
                            className="px-3 py-1 text-xs rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
                          >
                            Upravit v Skupina stanic
                          </button>
                          <button
                            onClick={() => setConfirmDel(g)}
                            className="px-3 py-1 text-xs rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                          >
                            Smazat
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedId === g.id && (
                      <GroupFiltersRow group={g} colSpan={COL_COUNT} navigate={navigate} />
                    )}
                  </Fragment>
                ))}
                {groups.length === 0 && (
                  <tr><td colSpan={COL_COUNT} className="px-4 py-8 text-center text-gray-400">Žádné uložené skupiny — nakonfigurujte skupinu ve „Skupina stanic“ a uložte ji tlačítkem „Uložit skupinu“.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm flex flex-col gap-4">
            <h3 className="text-lg font-bold text-gray-800">Smazat skupinu?</h3>
            <p className="text-sm text-gray-600">
              Opravdu chcete smazat skupinu <strong>{confirmDel.name}</strong>? Tato akce je nevratná.
            </p>
            {deleteMutation.isError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {deleteMutation.error?.message}
              </p>
            )}
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDel(null)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">
                Zrušit
              </button>
              <button
                onClick={() => deleteMutation.mutate(confirmDel.id)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                Smazat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
